import React, { ChangeEvent, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../../common/context/';
import { InterruptionModel } from '../../../../models';
import { changeDate, getErrorsValidationsDates } from '../../../../utils';
import { Modal } from '../BaseModal';
import { UpdateAndDeleteAdditionalHoursProps } from './UpdateAndDeleteAdditionalHours.Props';
import { InterruptionReferenceSelector } from '../../selectors/interruption/InterruptionReferenceSelector';
import { InterruptionReferenceTableSelector } from '../../selectors/interruption/InterruptionReferenceTableSelector';

const UpdateAndDeleteAdditionalHours = ({
    isOpen,
    employee,
    initDate,
    lastDate,
    additionals,
    saveChanges,
    setModalOpen,
}: UpdateAndDeleteAdditionalHoursProps) => {
    const { langResources, isSkinSmart, lang, interruptionReferenceTypesList } =
        useContext(AppContext);

    const [additionalsCopy, setAdditionalsCopy] = useState<InterruptionModel[][]>([
        ...additionals,
    ]);

    const [isEditing, setIsEditing] = useState(false);
    const [isAllDeleted, setIsAllDeleted] = useState(false);
    const [existingAnyError, setExistingAnyError] = useState(false);

    const { hourTypes } = useContext(AppContext);

    const getHourTpeName = (interruption: InterruptionModel) => {
        let result = hourTypes.find((type) => type.id == interruption.hourTypeId);

        return result?.abbreviature;
    };

    const handleChangeStartDate = (
        interruptionIndex: number,
        e: ChangeEvent<HTMLInputElement>,
        employeeId: number
    ) => {
        let additional = changeDate(
            true,
            additionalsCopy,
            interruptionIndex,
            e,
            employeeId
        );

        setAdditionalsCopy([...additional]);
    };

    const handleChangeEndDate = (
        interruptionIndex: number,
        e: ChangeEvent<HTMLInputElement>,
        employeeId: number
    ) => {
        let additional = changeDate(
            false,
            additionalsCopy,
            interruptionIndex,
            e,
            employeeId
        );
        setAdditionalsCopy([...additional]);
    };

    const handleDeleteAdditional = (
        interruptionIndex: number,
        employeeId: number,
        isMultiple: boolean
    ) => {
        let additional = additionalsCopy[employeeId][interruptionIndex];

        let additionalCopy = { ...additional };

        additionalCopy.removeInterruption = true;

        let copyAdditionalList = [...additionalsCopy];

        copyAdditionalList[employeeId][interruptionIndex] = additionalCopy;
        setAdditionalsCopy([...copyAdditionalList]);
    };

    const handleDeleteAllAdditional = (isMultiplex: boolean) => {
        setIsAllDeleted(true);
        setExistingAnyError(false);

        if (isMultiplex) {
            let additionalCopy = [...additionalsCopy];
            let AdditionalListToDelete: InterruptionModel[][] = [];

            additionalCopy.forEach((add, index) => {
                let newAdditional = add.map((i) => {
                    let additional = { ...i };
                    additional.removeInterruption = true;
                    return additional;
                });

                AdditionalListToDelete.push(newAdditional);
            });

            setAdditionalsCopy([...AdditionalListToDelete]);
        } else {
            let AdditionalListToDelete: InterruptionModel[] = [];

            additionalsCopy[0].map((add) => {
                let newAdditional = { ...add };
                newAdditional.removeInterruption = true;

                AdditionalListToDelete = [...AdditionalListToDelete, newAdditional];
            });

            setAdditionalsCopy([[...AdditionalListToDelete]]);
        }
    };

    const handlerChangeObservations = (
        { target }: ChangeEvent<HTMLInputElement>,
        interruptionIndex: number,
        employeeId: number
    ) => {
        let baseAdditionalCopy = [...additionalsCopy];

        let additionalCopy = [...additionalsCopy][employeeId];

        let interruptionCopy = additionalCopy.map((i) => {
            let additional = { ...i };

            if (additional.id === interruptionIndex) {
                additional.observations = target.value;
                return additional;
            }
            return additional;
        });

        baseAdditionalCopy[employeeId] = [...interruptionCopy];

        setAdditionalsCopy([...baseAdditionalCopy]);
    };

    const handlerCloseParentModal = () => {
        setModalOpen(false);
        setIsAllDeleted(false);
        setAdditionalsCopy(additionals);
    };

    const handlerSubmit = () => {
        if (!existingAnyError) {
            saveChanges(additionalsCopy, isAllDeleted);
        }
    };

    const handlerConfirmChanges = () => {
        let errors = getErrorsValidationsDates(additionalsCopy);

        if (errors?.length > 0) {
            toast.error(langResources['msg.validation.endDate'], {
                isLoading: false,
                autoClose: 5000,
                position: toast.POSITION.BOTTOM_RIGHT,
                closeOnClick: true,
                closeButton: true,
            });
            setIsEditing(true);
            setExistingAnyError(true);
            return;
        } else {
            setIsEditing(false);
            setExistingAnyError(false);
        }
    };

    const getInterruptionReferenceName = (idReference: number) => {
        let reference = interruptionReferenceTypesList.filter(
            (irt) => irt.id === idReference
        )[0];

        return reference ? reference.name : '-- --';
    };

    const handleChangeReferenceID = (
        value: string,
        interruptionIndex: number,
        employeeId: number
    ) => {
        let referenceId = Number(value);

        let baseInterruptionCopy = [...additionalsCopy];

        let interruptionByEmployeeIdCopy = [...additionalsCopy][employeeId];

        let interruptionCopy = interruptionByEmployeeIdCopy.map((i) => {
            let interruption = { ...i };

            if (interruption.id === interruptionIndex) {
                interruption!.interruptionReferenceId =
                    referenceId === -1 ? undefined : referenceId;

                return interruption;
            }
            return interruption;
        });

        baseInterruptionCopy[employeeId] = [...interruptionCopy];

        setAdditionalsCopy([...baseInterruptionCopy]);
    };

    return (
        <Modal
            isOpen={isOpen}
            modalTitle={
                langResources['update.delete.additional.time.modal']['title']
            }
            onClose={handlerCloseParentModal}
            onSubmit={handlerSubmit}
            widthCustom='1200px'
            disableSubmit={existingAnyError}
        >
            <div className='modal-body pb-1'>
                <div className='row mb-3'>
                    <div className='col-12'>
                        {additionalsCopy?.length > 1 ? (
                            <div>
                                <h5 className='text-muted'>
                                    {
                                        langResources[
                                            'update.delete.additional.time.modal'
                                        ]['startDate']
                                    }
                                    : {initDate}
                                </h5>
                                <h5 className='text-muted'>
                                    {
                                        langResources[
                                            'update.delete.additional.time.modal'
                                        ]['endDate']
                                    }
                                    : {lastDate}
                                </h5>
                            </div>
                        ) : (
                            <div>
                                <h5 className='fw-bolder'>
                                    {' '}
                                    {
                                        langResources[
                                            'update.delete.additional.time.modal'
                                        ]['employees.label']
                                    }
                                    :
                                </h5>
                                <h6>{additionalsCopy[0][0].employeeFullName}</h6>
                            </div>
                        )}
                    </div>
                </div>

                <div className='row align-items-center'>
                    <div className='col-8'>
                        <h5 className={`text-primary2`}>
                            {
                                langResources['update.delete.additional.time.modal'][
                                    'additional.time.list.label'
                                ]
                            }
                        </h5>
                    </div>

                    <div className='col-4 d-flex gap-2 align-items-center justify-content-end'>
                        {isEditing && (
                            <button
                                type='button'
                                title='Guardar'
                                className='btn btn-sm btn-success'
                                onClick={(e) => handlerConfirmChanges()}
                            >
                                <i className='mdi mdi-content-save'></i>
                                <span style={{ marginLeft: 5 }}>
                                    {' '}
                                    {
                                        langResources[
                                            'update.delete.additional.time.modal'
                                        ]['save.button']
                                    }
                                </span>
                            </button>
                        )}

                        {!isEditing && (
                            <button
                                title='Editar'
                                type='button'
                                className={`btn btn-sm btn-outline-primary d-flex align-items-center`}
                                onClick={(e) => {
                                    setIsEditing(true);
                                    setExistingAnyError(true);
                                }}
                            >
                                <i className='mdi mdi-pencil'></i>
                                <span style={{ marginLeft: 5 }}>
                                    {' '}
                                    {
                                        langResources[
                                            'update.delete.additional.time.modal'
                                        ]['edit.button']
                                    }
                                </span>
                            </button>
                        )}

                        <button
                            type='button'
                            className='btn btn-danger btn-sm d-flex align-items-center'
                            onClick={(e) => {
                                if (employee && employee?.length > 1) {
                                    handleDeleteAllAdditional(true);
                                } else {
                                    handleDeleteAllAdditional(false);
                                }
                            }}
                        >
                            <i className='ri-delete-bin-2-line p-0'></i>
                            <span style={{ marginLeft: 5 }}>
                                {' '}
                                {
                                    langResources[
                                        'update.delete.additional.time.modal'
                                    ]['delete.button']
                                }
                            </span>
                        </button>
                    </div>
                </div>
                <hr />

                <div className='w-100 d-flex mb-4'>
                    <div
                        className='row p-0 m-0'
                        style={{
                            maxHeight: '70vh',
                            overflowY: 'scroll',
                            width: '100%',
                        }}
                    >
                        <table className='table table-borderless w-100 text-dark'>
                            <thead
                                className=' text-black w-100 position-sticky p-0 m-0'
                                style={{ top: '0px', background: '#fff' }}
                            >
                                <tr
                                    className='text-center fs-6 '
                                    style={{
                                        height: 50,
                                        boxSizing: 'border-box',
                                        verticalAlign: 50,
                                        background: '#fff',
                                    }}
                                >
                                    {additionalsCopy?.length > 1 ? (
                                        <>
                                            <th
                                                scope='col'
                                                className=' py-2 fw-bold '
                                                style={{
                                                    width: 170,
                                                }}
                                            >
                                                {
                                                    langResources[
                                                        'update.delete.additional.time.modal'
                                                    ]['employees.label']
                                                }
                                            </th>

                                            <th
                                                scope='col'
                                                className='ww-100 py-2 fw-bold '
                                            >
                                                {
                                                    langResources[
                                                        'update.delete.additional.time.modal'
                                                    ]['additional.time.day.label']
                                                }
                                                <div className=' text-start mt-2'>
                                                    <span
                                                        className=' py-2 fw-bold '
                                                        style={{
                                                            marginLeft:
                                                                lang === 'en-US'
                                                                    ? 30
                                                                    : 20,
                                                        }}
                                                    >
                                                        {
                                                            langResources[
                                                                'update.delete.additional.time.modal'
                                                            ]['date.label']
                                                        }
                                                    </span>

                                                    <span
                                                        className=' py-2 fw-bold '
                                                        style={{
                                                            marginLeft:
                                                                lang == 'en-US'
                                                                    ? 70
                                                                    : 65,
                                                        }}
                                                    >
                                                        {
                                                            langResources[
                                                                'update.delete.additional.time.modal'
                                                            ][
                                                                'initial.date.end.date.label'
                                                            ]
                                                        }
                                                    </span>

                                                    <span
                                                        className=' py-2 fw-bold '
                                                        style={{
                                                            marginLeft:
                                                                lang == 'en-US'
                                                                    ? 75
                                                                    : 65,
                                                        }}
                                                    >
                                                        {
                                                            langResources[
                                                                'update.delete.additional.time.modal'
                                                            ][
                                                                'additional.time.name.label'
                                                            ]
                                                        }
                                                    </span>

                                                    <span
                                                        className='py-2 fw-bold '
                                                        style={{
                                                            marginLeft:
                                                                lang == 'en-US'
                                                                    ? 60
                                                                    : 52,
                                                        }}
                                                    >
                                                        {
                                                            langResources[
                                                                'update.delete.additional.time.modal'
                                                            ]['observation.label']
                                                        }
                                                    </span>

                                                    <span
                                                        className='py-2 fw-bold '
                                                        style={{
                                                            marginLeft: 52,
                                                        }}
                                                    >
                                                        {
                                                            langResources[
                                                                'update.delete.additional.time.modal'
                                                            ]['hour.label']
                                                        }
                                                    </span>

                                                    <span
                                                        className='py-2 fw-bold '
                                                        style={{
                                                            marginLeft: 52,
                                                        }}
                                                    >
                                                        {
                                                            langResources[
                                                                'update.delete.interruptions.modal'
                                                            ][
                                                                'interruption.reference.label'
                                                            ]
                                                        }
                                                    </span>

                                                    <span></span>
                                                </div>
                                            </th>
                                        </>
                                    ) : (
                                        <>
                                            <th
                                                scope='col'
                                                className=' py-2 fw-bold '
                                            >
                                                {
                                                    langResources[
                                                        'update.delete.additional.time.modal'
                                                    ]['date.label']
                                                }
                                            </th>
                                            <th
                                                scope='col'
                                                className=' py-2 fw-bold '
                                            >
                                                {
                                                    langResources[
                                                        'update.delete.additional.time.modal'
                                                    ]['initial.date.end.date.label']
                                                }
                                            </th>
                                            <th
                                                scope='col'
                                                className=' py-2 fw-bold '
                                            >
                                                {
                                                    langResources[
                                                        'update.delete.additional.time.modal'
                                                    ]['additional.time.name.label']
                                                }
                                            </th>
                                            <th
                                                scope='col'
                                                className='py-2 fw-bold '
                                            >
                                                {
                                                    langResources[
                                                        'update.delete.additional.time.modal'
                                                    ]['observation.label']
                                                }
                                            </th>
                                            <th
                                                scope='col'
                                                className='py-2 fw-bold '
                                            >
                                                {
                                                    langResources[
                                                        'update.delete.additional.time.modal'
                                                    ]['hour.label']
                                                }
                                            </th>
                                            <th
                                                scope='col'
                                                className='py-2 fw-bold '
                                            >
                                                {
                                                    langResources[
                                                        'update.delete.interruptions.modal'
                                                    ]['interruption.reference.label']
                                                }
                                            </th>
                                            <th></th>
                                        </>
                                    )}
                                </tr>
                            </thead>

                            <tbody>
                                {additionalsCopy?.length > 1
                                    ? additionalsCopy.map((add, id) => {
                                          return (
                                              <tr key={id}>
                                                  <th>
                                                      {
                                                          additionalsCopy[id][0]
                                                              .employeeFullName
                                                      }
                                                  </th>

                                                  <th>
                                                      {additionalsCopy[id].map(
                                                          (additional, addID) => (
                                                              <tr
                                                                  key={additional.id}
                                                                  className={`align-items-center text-center ${
                                                                      additionalsCopy[
                                                                          id
                                                                      ][addID]
                                                                          .removeInterruption &&
                                                                      'd-none'
                                                                  }`}
                                                              >
                                                                  <th className='p-1'>
                                                                      {
                                                                          additional.startDate.split(
                                                                              'T'
                                                                          )[0]
                                                                      }
                                                                  </th>

                                                                  <th className='d-flex p-1'>
                                                                      <input
                                                                          type='time'
                                                                          name='startDate'
                                                                          defaultValue={
                                                                              additional.startDate.split(
                                                                                  'T'
                                                                              )[1]
                                                                          }
                                                                          onChange={(
                                                                              e
                                                                          ) =>
                                                                              handleChangeStartDate(
                                                                                  additional.id,
                                                                                  e,
                                                                                  id
                                                                              )
                                                                          }
                                                                          className='form-control  form-control-sm  text-dark'
                                                                          readOnly={
                                                                              !isEditing
                                                                          }
                                                                      />
                                                                      -
                                                                      <input
                                                                          type='time'
                                                                          name='endDate'
                                                                          defaultValue={
                                                                              additional.endDate.split(
                                                                                  'T'
                                                                              )[1]
                                                                          }
                                                                          onChange={(
                                                                              e
                                                                          ) =>
                                                                              handleChangeEndDate(
                                                                                  additional.id,
                                                                                  e,
                                                                                  id
                                                                              )
                                                                          }
                                                                          className='form-control  form-control-sm  text-dark'
                                                                          readOnly={
                                                                              !isEditing
                                                                          }
                                                                      />
                                                                  </th>

                                                                  <th className='p-1'>
                                                                      {
                                                                          langResources[
                                                                              'update.delete.additional.time.modal'
                                                                          ][
                                                                              'additional.hour.title'
                                                                          ]
                                                                      }
                                                                  </th>

                                                                  <th
                                                                      className='p-1'
                                                                      title={
                                                                          additional.observations
                                                                      }
                                                                      style={{
                                                                          width: 100,
                                                                          maxWidth: 100,
                                                                          maxHeight: 50,
                                                                          overflow:
                                                                              'hidden',
                                                                      }}
                                                                  >
                                                                      <input
                                                                          type='text'
                                                                          className='form-control  form-control-sm w-100 h-100 text-black'
                                                                          value={
                                                                              additional.observations
                                                                          }
                                                                          disabled={
                                                                              !isEditing
                                                                          }
                                                                          onChange={(
                                                                              e
                                                                          ) =>
                                                                              handlerChangeObservations(
                                                                                  e,
                                                                                  additional.id,
                                                                                  id
                                                                              )
                                                                          }
                                                                      />
                                                                  </th>

                                                                  <th className='p-1'>
                                                                      <InterruptionReferenceTableSelector
                                                                          name=''
                                                                          onChange={(
                                                                              e
                                                                          ) =>
                                                                              handleChangeReferenceID(
                                                                                  e,
                                                                                  additional.id,
                                                                                  id
                                                                              )
                                                                          }
                                                                          value={
                                                                              additional.interruptionReferenceId
                                                                          }
                                                                          disable
                                                                          label={getInterruptionReferenceName(
                                                                              additional.interruptionReferenceId ??
                                                                                  0
                                                                          )}
                                                                      />
                                                                  </th>

                                                                  <th
                                                                      className='p-1'
                                                                      style={{
                                                                          minWidth: 120,
                                                                      }}
                                                                  >
                                                                      {getHourTpeName(
                                                                          additional
                                                                      )}
                                                                  </th>

                                                                  <th className='p-1'>
                                                                      <button
                                                                          type='button'
                                                                          onClick={(
                                                                              e
                                                                          ) =>
                                                                              handleDeleteAdditional(
                                                                                  addID,
                                                                                  id,
                                                                                  true
                                                                              )
                                                                          }
                                                                          style={{
                                                                              padding:
                                                                                  '1px 2px',
                                                                          }}
                                                                          className='btn btn-danger btn-sm'
                                                                          disabled={
                                                                              !isEditing
                                                                          }
                                                                      >
                                                                          <i
                                                                              className='ri-delete-bin-2-line p-0'
                                                                              style={{
                                                                                  fontSize: 18,
                                                                              }}
                                                                          ></i>
                                                                      </button>
                                                                  </th>
                                                              </tr>
                                                          )
                                                      )}
                                                  </th>
                                              </tr>
                                          );
                                      })
                                    : additionalsCopy[0].map((additional, index) => {
                                          return (
                                              <tr
                                                  key={additional.id}
                                                  style={{
                                                      height: 2.5,
                                                      lineHeight: 2.5,
                                                  }}
                                                  className={`align-items-center text-center ${
                                                      additionalsCopy[0][index]
                                                          .removeInterruption &&
                                                      'd-none'
                                                  }`}
                                              >
                                                  <th className='p-1'>
                                                      {
                                                          additional.startDate.split(
                                                              'T'
                                                          )[0]
                                                      }
                                                  </th>

                                                  <th className='d-flex p-1'>
                                                      <input
                                                          type='time'
                                                          name='startDate'
                                                          defaultValue={
                                                              additional.startDate.split(
                                                                  'T'
                                                              )[1]
                                                          }
                                                          onChange={(e) =>
                                                              handleChangeStartDate(
                                                                  additional.id,
                                                                  e,
                                                                  0
                                                              )
                                                          }
                                                          className='form-control  form-control-sm  text-dark'
                                                          readOnly={!isEditing}
                                                      />
                                                      -
                                                      <input
                                                          type='time'
                                                          name='endDate'
                                                          defaultValue={
                                                              additional.endDate.split(
                                                                  'T'
                                                              )[1]
                                                          }
                                                          onChange={(e) =>
                                                              handleChangeEndDate(
                                                                  additional.id,
                                                                  e,
                                                                  0
                                                              )
                                                          }
                                                          className='form-control  form-control-sm  text-dark'
                                                          readOnly={!isEditing}
                                                      />
                                                  </th>

                                                  <th className='p-1'>
                                                      {
                                                          langResources[
                                                              'update.delete.additional.time.modal'
                                                          ]['additional.hour.title']
                                                      }
                                                  </th>

                                                  <th
                                                      className='p-1'
                                                      title={additional.observations}
                                                      style={{
                                                          width: 100,
                                                          maxWidth: 100,
                                                          maxHeight: 50,
                                                          overflow: 'hidden',
                                                      }}
                                                  >
                                                      <input
                                                          type='text'
                                                          className='form-control  form-control-sm w-100 h-100 text-black'
                                                          value={
                                                              additional.observations
                                                          }
                                                          disabled={!isEditing}
                                                          onChange={(e) =>
                                                              handlerChangeObservations(
                                                                  e,
                                                                  additional.id,
                                                                  0
                                                              )
                                                          }
                                                      />
                                                  </th>

                                                  <th
                                                      className='p-1'
                                                      style={{
                                                          minWidth: 120,
                                                      }}
                                                  >
                                                      {getHourTpeName(additional)}
                                                  </th>

                                                  <th className='p-1'>
                                                      <InterruptionReferenceTableSelector
                                                          name=''
                                                          onChange={(e) =>
                                                              handleChangeReferenceID(
                                                                  e,
                                                                  additional.id,
                                                                  0
                                                              )
                                                          }
                                                          value={
                                                              additional.interruptionReferenceId
                                                          }
                                                          disable
                                                          label={getInterruptionReferenceName(
                                                              additional.interruptionReferenceId ??
                                                                  0
                                                          )}
                                                      />
                                                  </th>

                                                  <th className='p-1'>
                                                      <button
                                                          type='button'
                                                          onClick={(e) =>
                                                              handleDeleteAdditional(
                                                                  index,
                                                                  0,
                                                                  false
                                                              )
                                                          }
                                                          style={{
                                                              padding: '1px 2px',
                                                          }}
                                                          className='btn btn-danger btn-sm'
                                                          disabled={!isEditing}
                                                      >
                                                          <i
                                                              className='ri-delete-bin-2-line p-0'
                                                              style={{
                                                                  fontSize: 18,
                                                              }}
                                                          ></i>
                                                      </button>
                                                  </th>
                                              </tr>
                                          );
                                      })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default UpdateAndDeleteAdditionalHours;
